import moment from 'moment';

export const isDateExistsInRecurringShift = (_date, shiftData) => {
    if (
        shiftData.shiftType !== 'recurring' ||
        !shiftData?.shiftDate ||
        !shiftData?.repeatOn?.length
    ) {
        return false;
    }
    const date = new Date(_date);
    const shiftDate = new Date(
        moment(shiftData.shiftDate).format('YYYY-MM-DD'),
    );
    const shiftDay = moment(date).format('dddd');
    if (
        date >= shiftDate &&
        shiftData.repeatOn.includes(shiftDay) &&
        moment(date).diff(moment(shiftDate), 'weeks') %
            shiftData?.repeatEvery ===
            0
    ) {
        return true;
    }
    return false;
};

export const extractHoursFromShiftTime = shiftTime => {
    return parseInt(shiftTime);
};

export const extractMinutesFromShiftTime = shiftTime => {
    const mins = shiftTime - extractHoursFromShiftTime(shiftTime);
    if (mins === 0.25) {
        return 15;
    } else if (mins === 0.5) {
        return 30;
    } else if (mins === 0.75) {
        return 45;
    }
    return 0;
};

/**
 * Formats transit allowance display with proper singular/plural formatting
 * @param {number|string} value - The transit allowance value in hours
 * @returns {string} Formatted transit allowance string
 */
export const formatTransitAllowanceDisplay = value => {
    if (!value || value === '0' || value === 0) return 'N/A';

    const numValue = parseFloat(value);
    if (numValue === 0.5) return '30 minutes';
    if (numValue === 1) return '1 hour';

    return `${numValue} hours`;
};

/**
 * Calculates the total time worked including breaks for a given tracker
 * @param {Object} tracker - The tracker object containing start, end, breaks, and state
 * @returns {number} Total time worked in milliseconds
 */
export const calculateTotalTimeWorkedWithPause = tracker => {
    if (!tracker) return 0;

    // Create a copy to avoid modifying the original object
    const trackerCopy = { ...tracker };
    trackerCopy.breaks = (tracker.breaks || []).filter(
        unpaidBreak => unpaidBreak.end !== 0,
    );

    if (trackerCopy.end) {
        return calculateCompletedShiftTime(trackerCopy);
    } else {
        return calculateOngoingShiftTime(trackerCopy);
    }
};

/**
 * Calculates time for a completed shift (has end time)
 * @param {Object} tracker - The tracker object
 * @returns {number} Time in milliseconds
 */
const calculateCompletedShiftTime = tracker => {
    if (!tracker.breaks.length) {
        return moment(tracker.end * 1000).diff(
            tracker.start * 1000,
            'milliseconds',
        );
    }

    if (tracker.breaks[0].end === 0) {
        return moment(tracker.end * 1000).diff(
            tracker.start * 1000,
            'milliseconds',
        );
    }

    let time = 0;

    // Add time from last break end to shift end
    if (tracker.breaks[tracker.breaks.length - 1].end) {
        time += moment(tracker.end * 1000).diff(
            tracker.breaks[tracker.breaks.length - 1].end * 1000,
            'milliseconds',
        );
    }

    // Add time between breaks
    for (let i = tracker.breaks.length - 1; i >= 0; i--) {
        if (i === 0) {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.start * 1000,
                'milliseconds',
            );
        } else {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.breaks[i - 1].end * 1000,
                'milliseconds',
            );
        }
    }

    return time;
};

/**
 * Calculates time for an ongoing shift (no end time)
 * @param {Object} tracker - The tracker object
 * @returns {number} Time in milliseconds
 */
const calculateOngoingShiftTime = tracker => {
    if (!tracker.breaks.length) {
        return moment().diff(tracker.start * 1000, 'milliseconds');
    }

    let time = 0;

    // If tracker is in 'played' state, add time from last break end to now
    if (tracker.state === 'played') {
        time += moment().diff(
            tracker.breaks[tracker.breaks.length - 1].end * 1000,
            'milliseconds',
        );
    }

    // Add time between breaks
    for (let i = tracker.breaks.length - 1; i >= 0; i--) {
        if (i === 0) {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.start * 1000,
                'milliseconds',
            );
        } else {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.breaks[i - 1].end * 1000,
                'milliseconds',
            );
        }
    }

    return time;
};

/**
 * Formats milliseconds to HH:MM:SS format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time string (e.g., "8:30:45")
 */
export const formatTimeFromMilliseconds = milliseconds => {
    if (!milliseconds || milliseconds < 0) return '00:00:00';

    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Validates tracker time format
 * @param {string} timeString - Time in HH:mm:ss format
 * @returns {Object} Validation result with isValid and error message
 */
export const validateTrackerTime = timeString => {
    if (!timeString) {
        return { isValid: false, error: 'Time is required' };
    }

    // Validate time format (HH:mm:ss)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    if (!timeRegex.test(timeString)) {
        return { isValid: false, error: 'Invalid time format. Use HH:mm:ss' };
    }

    return { isValid: true, error: null };
};

/**
 * Calculates shift end time based on start time and duration
 * @param {number} startTime - Unix timestamp of shift start
 * @param {string} duration - Duration in HH:mm:ss format
 * @returns {number} Unix timestamp of shift end
 */
export const calculateShiftEndTime = (startTime, duration) => {
    const durationSeconds = moment.duration(duration).asSeconds();
    return startTime + durationSeconds;
};
